/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import isString from "lodash/isString";
import { handleUnauthorizedResponseError, logoutIfNoToken, cleanSessionAndRedirect } from "../auth/utils";

export const getFile = async ({ url, urlParams, headers = {}, arrayBuffer = false }) => {
  logoutIfNoToken();
  const urlParamsString = new URLSearchParams(urlParams);
  url = encodeURI(url);
  const lS = window.localStorage.getItem("genToken") || "{}";

  try {
    const response = await fetch(url + (urlParams ? "?" + urlParamsString : ""), {
      method: "GET",
      headers: { token: JSON.parse(lS)["token"], ...headers },
    });
    if (!response.ok) {
      throw response;
    }
    return arrayBuffer ? response.arrayBuffer() : response.blob();
  } catch (error) {
    handleUnauthorizedResponseError(error);
    throw error;
  }
};

const handleErrorStatus = (status) => {
  switch (status) {
    case 403:
      cleanSessionAndRedirect();
      break;
  }
};

export const getV2 = async ({ url, params, headers = {}, includeResponseHeaders = null, options }) => {
  logoutIfNoToken();
  const urlParams = new URLSearchParams(params);
  let apiUrl = encodeURI(url);

  const genToken = JSON.parse(localStorage.getItem("genToken") || "{}");
  if (params) {
    apiUrl = `${apiUrl}?${urlParams}`;
  }

  const requestBody = {
    method: "GET",
    headers: { token: genToken["token"], "Content-Type": "application/json", ...headers },
    ...options,
  };

  try {
    const response = await fetch(apiUrl, requestBody);
    if (!response.ok) {
      handleErrorStatus(response.status);
    } else {
      let json = {};
      const plainText = await response.text();
      if (!plainText) {
        json = "";
      } else {
        // The response was a JSON object
        json = JSON.parse(plainText);
      }

      if (includeResponseHeaders) {
        let responseHeaders = {};
        includeResponseHeaders.forEach((header) => {
          responseHeaders[header] = response.headers.get(header);
        });
        return { data: json, responseHeaders };
      }

      return { data: json };
    }
  } catch (error) {
    console.log(error.message);
    throw error;
  }
};

export const get = async ({ url, urlParams, headers = {}, includeResponseHeaders = null, options = {} }) => {
  logoutIfNoToken();
  const urlParamsString = new URLSearchParams(urlParams);
  url = encodeURI(url);

  const genToken = window.localStorage.getItem("genToken") || "{}";

  let apiUrl = url;
  if (urlParams) {
    apiUrl = `${apiUrl}?${urlParamsString}`;
  }

  const requestBody = {
    method: "GET",
    headers: { token: JSON.parse(genToken)["token"], "Content-Type": "application/json", ...headers },
    ...options,
  };

  try {
    const response = await fetch(apiUrl, requestBody);
    const json = await response.json();

    if (response.ok) {
      if (includeResponseHeaders) {
        let headers = {};
        includeResponseHeaders.forEach((header) => {
          headers[header] = response.headers.get(header);
        });
        return { data: json, responseHeaders: headers };
      }
      return json;
    } else {
      throw json;
    }
  } catch (error) {
    handleUnauthorizedResponseError(error);
    throw error;
  }
};

export const getTypeTextJson = async ({ url, options = {} }) => {
  logoutIfNoToken();
  const genToken = window.localStorage.getItem("genToken") || "{}";

  let apiUrl = url;

  const requestBody = {
    method: "GET",
    headers: { token: JSON.parse(genToken)["token"], "Content-Type": "application/json" },
    ...options,
  };

  try {
    const response = await fetch(apiUrl, requestBody);
    if (response.ok) {
      const responseText = await response.text();
      const dataJson = responseText ? JSON.parse(responseText) : null;
      return dataJson;
    } else {
      throw null;
    }
  } catch (error) {
    handleUnauthorizedResponseError(error);
    throw error;
  }
};

export const post = async (url, data, headers = {}, hasToken, options = {}) => {
  logoutIfNoToken();
  const isFormData = data instanceof FormData;

  const genToken = window.localStorage.getItem("genToken") || "{}";
  headers = { token: JSON.parse(genToken)["token"], ...headers };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const requestBody = {
    method: "POST",
    headers,
    body: isFormData ? data : JSON.stringify(data),
    ...options,
  };

  let apiURl = url;
  if (hasToken) {
    apiURl = `${apiURl}?token=${headers.token}`;
  }

  try {
    const response = await fetch(apiURl, requestBody);
    const text = await response.text();
    if (response.ok) {
      if (text.length) return JSON.parse(text);
    } else {
      throw text;
    }
  } catch (error) {
    handleUnauthorizedResponseError(error);
    throw error;
  }
};

export const put = async (url, data) => {
  logoutIfNoToken();
  let body;
  const genToken = window.localStorage.getItem("genToken") || "{}";
  let headers = { token: JSON.parse(genToken)["token"] };
  const isFormData = data instanceof FormData;

  if (isFormData || isString(data)) {
    body = data;
  } else {
    body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: isFormData ? headers : { ...headers, "Content-Type": "application/json" },
      body,
    });

    let responseData;
    try {
      responseData = await (isFormData ? response.text() : response.json());
    } catch (err) {
      console.log(err);
    }
    if (response.ok) {
      return responseData;
    } else {
      throw responseData;
    }
  } catch (error) {
    handleUnauthorizedResponseError(error);
    throw error;
  }
};

export const deleteReq = async (url, data) => {
  logoutIfNoToken();
  const lS = window.localStorage.getItem("genToken") || "{}";
  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: { token: JSON.parse(lS)["token"], "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    let json;
    try {
      json = await response.json();
    } catch (err) {
      console.log(err);
    }
    if (response.ok) {
      return json;
    } else {
      throw json;
    }
  } catch (error) {
    handleUnauthorizedResponseError(error);
    throw error;
  }
};

const token = () => {
  const lS = window.localStorage.getItem("genToken") || "{}";
  return JSON.parse(lS).token;
};

const getSecurityHeader = () => {
  const lS = window.localStorage.getItem("genToken");
  return {
    token: JSON.parse(lS)["token"],
    "Content-Type": "application/json",
  };
};

export const getPdfQueueStatus = async (locationId) => {
  const newPDFMicroservice = await fetchHelper.get({
    url: `/patientmanagement/rest/location/${locationId}/pdf-queue-microservice-toggle`,
  });

  return newPDFMicroservice.pdfQueueMicroservice;
};

const fetchHelper = {
  getFile,
  get,
  getV2,
  getTypeTextJson,
  post,
  put,
  token,
  getSecurityHeader,
  delete: deleteReq,
  getPdfQueueStatus,
};

export default fetchHelper;
